import React from "react";
import { motion } from "framer-motion";
import { Target, Eye, Sparkles, GraduationCap, Briefcase } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Counter } from "../components/Counter";
import { STATS, OWNERS, FOUNDER_LEGACY } from "../data/site";

export default function AboutPage({ onQuoteClick }) {
  return (
    <div data-testid="about-page">
      <Seo title="About — IDEAL ENTERPRISES" description="Trusted infrastructure contractor delivering gas pipeline construction services across India since 1998." />
      <PageHeader
        overline="ABOUT US"
        title="A trusted partner for India's gas distribution and utility infrastructure."
        subtitle="Founded in 1998, IDEAL ENTERPRISES has grown from a telecom OFC contractor into a specialist delivering safe, on-spec CGD pipeline construction across India."
        bgImage="https://images.pexels.com/photos/8961146/pexels-photo-8961146.jpeg"
      />

      {/* OUR STORY */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="overline">Our Story</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-blue mt-4">
              From a telecom contractor to a CGD specialist — three decades, one discipline.
            </h2>
            <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">
              <p>
                IDEAL ENTERPRISES was established in <span className="font-semibold text-brand-blue">1998</span> by our late father, <span className="font-semibold text-brand-blue">Shri Krishnasanehi Shivshankar Tiwari</span>, who pioneered the company's early work laying optical fibre cable networks for telecom operators across India.
              </p>
              <p>
                His vision, integrity, and engineering discipline remain the foundation on which the next generation has built our expansion into City Gas Distribution, steel pipeline construction, and utility infrastructure.
              </p>
              <p>
                Today we work with India's leading CGD operators, EPC majors, and government utility clients — equally comfortable laying domestic PNG networks in a township as we are executing a multi-package steel grid for a Geographic Area authorisation.
              </p>
              <p>
                What hasn't changed: an obsession with documentation, a refusal to compromise on HSE, and an engineering culture that hires for curiosity as much as competence.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-[#F8F9FA] border border-slate-200 p-8">
              <p className="overline">By the Numbers</p>
              <div className="mt-6 grid grid-cols-2 gap-6">
                {STATS.slice(0, 4).map((s) => (
                  <div key={s.label}>
                    <Counter value={s.value} suffix={s.suffix} />
                    <p className="text-sm text-slate-600 mt-2 font-medium">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER LEGACY */}
      <section className="py-20 bg-brand-blue text-white relative overflow-hidden" data-testid="founder-legacy">
        <div className="absolute inset-0 blueprint-grid opacity-10" />
        <div className="max-w-5xl mx-auto px-6 lg:px-10 relative">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-4 flex flex-col items-center md:items-start">
              <div className="relative">
                <div className="w-48 h-60 md:w-56 md:h-72 overflow-hidden border-2 border-brand-orange/40 shadow-2xl shadow-black/30">
                  <img
                    src="/team/founder.jpg"
                    alt="Late Shri Krishnasanehi Shivshankar Tiwari — Founder"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    data-testid="founder-portrait"
                  />
                </div>
                <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 md:left-4 md:translate-x-0 font-mono-tech text-[10px] tracking-[0.25em] bg-brand-orange text-white px-3 py-1 shadow-lg">
                  IN MEMORIAM
                </span>
              </div>
            </div>
            <div className="md:col-span-8">
              <p className="overline text-brand-orange">Our Founder</p>
              <h2 className="font-heading font-bold text-2xl md:text-3xl mt-3 leading-tight">
                <span className="text-brand-orange">Late</span> {FOUNDER_LEGACY.name}
              </h2>
              <p className="font-mono-tech text-xs tracking-wider text-white/60 mt-2">
                FOUNDER · EST. {FOUNDER_LEGACY.yearFounded} · TELECOM &amp; OFC INFRASTRUCTURE
              </p>
              <p className="text-white/80 mt-6 leading-relaxed">
                {FOUNDER_LEGACY.legacy}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION / VISION / VALUES */}
      <section className="py-20 bg-[#F8F9FA] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-px bg-slate-200">
          {[
            { Icon: Target, t: "Our Mission", d: "Engineer, build, and maintain the below-ground infrastructure that powers a cleaner, gas-fueled India." },
            { Icon: Eye, t: "Our Vision", d: "To be the most trusted specialist partner for City Gas Distribution and utility infrastructure across the Indian sub-continent." },
            { Icon: Sparkles, t: "Our Values", d: "Safety without exception, documentation as discipline, and engineering humility on every site." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="bg-white p-10">
              <Icon size={30} className="text-brand-orange" />
              <h3 className="font-heading font-bold text-2xl text-brand-blue mt-6">{t}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MEET THE LEADERSHIP — KNOW THE OWNERS */}
      <section className="py-24 bg-white" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <p className="overline">Know the Owners</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-blue mt-3">
              Meet the leadership behind Ideal Enterprises.
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              A second-generation leadership team carrying forward a family legacy — combining engineering rigour, business acumen, and a quiet, technical confidence honed over years of field execution.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
            {OWNERS.map((o, i) => (
              <motion.article
                key={o.name}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group relative bg-white border border-slate-200 hover:border-brand-orange transition-all duration-300 btn-lift overflow-hidden"
                data-testid={`owner-card-${i}`}
              >
                <span className="absolute top-0 left-0 right-0 h-1 bg-brand-orange scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />

                <div className="grid grid-cols-3">
                  {/* Portrait placeholder */}
                  <div className="col-span-1 bg-brand-blue relative overflow-hidden min-h-[280px]">
                    <div className="absolute inset-0 blueprint-grid opacity-15" />
                    <div className="absolute inset-0 grid place-items-center">
                      <div className="text-center">
                        <div className="font-heading font-black text-5xl md:text-6xl text-white tracking-tight">
                          {o.initials}
                        </div>
                        <div className="mt-3 mx-auto w-12 h-0.5 bg-brand-orange" />
                        <p className="font-mono-tech text-[9px] tracking-[0.25em] text-white/60 mt-3">
                          PORTRAIT
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="col-span-2 p-7 md:p-8">
                    <p className="font-mono-tech text-[10px] tracking-[0.2em] text-brand-orange">
                      DIRECTOR / {(i + 1).toString().padStart(2, "0")}
                    </p>
                    <h3 className="font-heading font-bold text-xl md:text-2xl text-brand-blue mt-3 leading-tight">
                      {o.name}
                    </h3>
                    <p className="text-sm text-slate-500 font-medium mt-1">{o.role}</p>

                    <div className="mt-5">
                      <p className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-400 mb-2 inline-flex items-center gap-1.5">
                        <GraduationCap size={12} /> QUALIFICATIONS
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {o.qualifications.map((q) => (
                          <span
                            key={q}
                            className="text-[11px] font-medium text-brand-blue bg-orange-50 border border-orange-200 px-2.5 py-1"
                            data-testid={`owner-qual-${i}`}
                          >
                            {q}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-5">
                      <p className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-400 mb-2 inline-flex items-center gap-1.5">
                        <Briefcase size={12} /> FOCUS AREAS
                      </p>
                      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
                        {o.focus.map((f) => (
                          <li key={f} className="inline-flex items-center gap-1.5 before:content-[''] before:w-1 before:h-1 before:bg-brand-orange before:rounded-full">
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Profile text full-width */}
                <div className="px-7 md:px-8 pb-7 md:pb-8 -mt-2">
                  <p className="text-sm text-slate-700 leading-relaxed border-t border-slate-200 pt-5">
                    {o.profile}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F8F9FA] border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-blue">
            Ready to discuss your next package?
          </h2>
          <p className="text-slate-600 mt-4">Quote turnaround within one business day.</p>
          <button
            onClick={onQuoteClick}
            data-testid="about-page-quote"
            className="mt-8 bg-brand-orange text-white px-8 py-4 font-semibold btn-lift hover:bg-orange-600"
          >
            Request a Quote
          </button>
        </div>
      </section>
    </div>
  );
}
